'use client';

import { useMemo, useState, useCallback } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { useTranslations } from 'next-intl';
import { Tick01Icon, Copy01Icon } from '@terra/ui-icons';
import type {
  AuthMaterialArtifactKind,
  CachedCredentialArtifactSummary,
} from '@terra/shared-contracts-authenticator';
import { buildMaskedValuePreview } from '@terra/shared-utils';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Button } from '../../components/button';
import { IconButton } from '../../components/icon-button';
import { Collapsible } from '../../components/collapsible';
import { Tooltip } from '../../components/tooltip';
import {
  BodyDefault,
  BodyStrong,
  MetaDefault,
  MonoDefault,
  MonoSmall,
} from '../../components/typography';
import { SensitiveInput } from '../inputs/SensitiveInput';
import { writeTextToClipboard } from '../http-editor/clipboardWriter';

export type GatheredArtifactItem = CachedCredentialArtifactSummary & {
  value?: string;
};

const MASKED_VALUE_PREVIEW = buildMaskedValuePreview();

const getArtifactPlaintextValue = (
  artifact: GatheredArtifactItem,
): string | undefined => {
  const value = artifact.value;

  if (typeof value !== 'string' || value.length === 0) {
    return undefined;
  }

  if (value === MASKED_VALUE_PREVIEW || /^[•*]+$/.test(value)) {
    return undefined;
  }

  return value;
};

type GatheredArtifactsPanelProps = {
  artifacts: GatheredArtifactItem[];
  mode: 'masked' | 'revealed';
  readiness?: 'ready' | 'reverify';
  onReverify?: () => void;
  isReverifyDisabled?: boolean;
};
const ARTIFACT_GROUPS: AuthMaterialArtifactKind[] = [
  'header',
  'cookie',
  'secret',
  'localStorage',
  'sessionStorage',
];

function ArtifactValueCell({
  artifact,
  mode,
}: {
  artifact: GatheredArtifactItem;
  mode: 'masked' | 'revealed';
}) {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const fieldKey = `${artifact.kind}:${artifact.name}`;

  if (mode === 'masked') {
    return (
      <MonoDefault>{artifact.valuePreview || MASKED_VALUE_PREVIEW}</MonoDefault>
    );
  }

  const plaintextValue = getArtifactPlaintextValue(artifact);

  if (!plaintextValue) {
    return (
      <MonoDefault>{artifact.valuePreview || MASKED_VALUE_PREVIEW}</MonoDefault>
    );
  }

  return (
    <SensitiveInput
      value={plaintextValue}
      placeholder=""
      onChange={() => undefined}
      fieldKey={fieldKey}
      allowViewingCredentials={true}
      copiedState={copied}
      visibleState={visible}
      onToggleVisibility={() => setVisible((current) => !current)}
      onCopySuccess={() => setCopied(true)}
      onCopy={async (copyValue) => {
        await navigator.clipboard.writeText(copyValue);
      }}
      type="password"
      isDefaultValue={false}
      disabled={true}
    />
  );
}

export function GatheredArtifactsPanel({
  artifacts,
  mode,
  readiness,
  onReverify,
  isReverifyDisabled = false,
}: GatheredArtifactsPanelProps) {
  const t = useTranslations(
    'designSystem.applicationCredentials.gatheredArtifacts',
  );
  const groupedArtifacts = useMemo(() => {
    return ARTIFACT_GROUPS.map((kind) => ({
      kind,
      items: artifacts.filter((artifact) => artifact.kind === kind),
    })).filter((group) => group.items.length > 0);
  }, [artifacts]);

  if (artifacts.length === 0) {
    return (
      <VStack alignItems="stretch" gap={2} py={2}>
        <BodyDefault color="fg.muted">{t('emptyState')}</BodyDefault>
      </VStack>
    );
  }

  const showReverifyAction =
    onReverify &&
    (readiness === 'reverify' ||
      artifacts.some((artifact) => {
        if (!artifact.expiresAt) {
          return false;
        }

        return new Date(artifact.expiresAt).getTime() <= Date.now();
      }));

  return (
    <VStack alignItems="stretch" gap={3} py={2}>
      <BodyStrong>{t('title')}</BodyStrong>

      {groupedArtifacts.map((group) => (
        <Collapsible.Root key={group.kind} defaultOpen>
          <Collapsible.Trigger>
            <HStack justifyContent="space-between" w="full">
              <BodyStrong>{t(`groups.${group.kind}`)}</BodyStrong>
              <MetaDefault color="fg.muted">{group.items.length}</MetaDefault>
            </HStack>
          </Collapsible.Trigger>
          <Collapsible.Content>
            <VStack alignItems="stretch" gap={2} pt={2}>
              {group.items.map((artifact) => (
                <Box
                  key={`${artifact.kind}:${artifact.name}`}
                  borderWidth="1px"
                  borderColor="border.neutral.subtle"
                  borderRadius="md"
                  p={3}
                >
                  <VStack alignItems="stretch" gap={2}>
                    <HStack justifyContent="space-between" gap={3}>
                      <MetaDefault color="fg.muted">
                        {t('columns.name')}
                      </MetaDefault>
                      <BodyStrong truncate>{artifact.name}</BodyStrong>
                    </HStack>
                    <HStack
                      justifyContent="space-between"
                      gap={3}
                      alignItems="flex-start"
                    >
                      <MetaDefault color="fg.muted" flexShrink={0}>
                        {t('columns.value')}
                      </MetaDefault>
                      <Box flex={1} minW={0}>
                        <ArtifactValueCell artifact={artifact} mode={mode} />
                      </Box>
                    </HStack>
                    {artifact.expiresAt ? (
                      <HStack justifyContent="space-between" gap={3}>
                        <MetaDefault color="fg.muted">
                          {t('columns.expires')}
                        </MetaDefault>
                        <BodyDefault>
                          {formatDistanceToNow(new Date(artifact.expiresAt), {
                            addSuffix: true,
                          })}
                        </BodyDefault>
                      </HStack>
                    ) : null}
                  </VStack>
                </Box>
              ))}
            </VStack>
          </Collapsible.Content>
        </Collapsible.Root>
      ))}

      {showReverifyAction ? (
        <Button
          variant="outline"
          size="sm"
          onClick={onReverify}
          disabled={isReverifyDisabled}
          alignSelf="flex-start"
        >
          {t('reverify')}
        </Button>
      ) : null}
    </VStack>
  );
}

function CopyableArtifactRow({
  artifact,
  hideCopy = false,
}: {
  artifact: GatheredArtifactItem;
  hideCopy?: boolean;
}) {
  const t = useTranslations(
    'designSystem.applicationCredentials.gatheredArtifacts',
  );
  const tCommon = useTranslations('designSystem.common');
  const [copied, setCopied] = useState(false);
  const plaintextValue = getArtifactPlaintextValue(artifact);
  const displayValue =
    plaintextValue ?? artifact.valuePreview ?? MASKED_VALUE_PREVIEW;
  const canCopy = Boolean(plaintextValue) && !hideCopy;
  const handleCopy = useCallback(async () => {
    if (!plaintextValue) {
      return;
    }

    await writeTextToClipboard(plaintextValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [plaintextValue]);

  return (
    <HStack gap={2} alignItems="center" w="full" overflow="visible">
      <VStack alignItems="stretch" gap={0.5} flex={1} minW={0}>
        <MonoSmall truncate>{artifact.name}</MonoSmall>
        <MonoSmall color="fg.muted" truncate>
          {displayValue}
        </MonoSmall>
      </VStack>
      {canCopy ? (
        <Tooltip
          title={t('copyArtifact', { name: artifact.name })}
          positioning={{ strategy: 'fixed' }}
        >
          <IconButton
            variant="ghost"
            size="xs"
            flexShrink={0}
            stopPropagation
            aria-label={tCommon('copy')}
            onClick={() => {
              void handleCopy();
            }}
            color={copied ? 'fg.success' : undefined}
          >
            {copied ? <Tick01Icon size={14} /> : <Copy01Icon size={14} />}
          </IconButton>
        </Tooltip>
      ) : null}
    </HStack>
  );
}

type GatheredArtifactsTooltipContentProps = {
  artifacts: GatheredArtifactItem[];
  showEmptyState?: boolean;
  hideCopy?: boolean;
};

export function GatheredArtifactsTooltipContent({
  artifacts,
  showEmptyState = false,
  hideCopy = false,
}: GatheredArtifactsTooltipContentProps) {
  const t = useTranslations(
    'designSystem.applicationCredentials.gatheredArtifacts',
  );
  const groupedArtifacts = useMemo(() => {
    return ARTIFACT_GROUPS.map((kind) => ({
      kind,
      items: artifacts.filter((a) => a.kind === kind),
    })).filter((group) => group.items.length > 0);
  }, [artifacts]);

  if (artifacts.length === 0) {
    if (!showEmptyState) return null;

    return <MetaDefault color="fg.muted">{t('emptyState')}</MetaDefault>;
  }

  return (
    <VStack gap={2} alignItems="stretch" w="full">
      <BodyStrong>{t('title')}</BodyStrong>
      <VStack
        gap={2}
        alignItems="stretch"
        maxH="260px"
        overflowY="auto"
        pr={1}
        css={{
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '4px',
          },
        }}
      >
        {groupedArtifacts.map((group) => (
          <VStack key={group.kind} gap={1} alignItems="stretch">
            <MetaDefault color="fg.muted">
              {t(
                `groups.${group.kind as 'header' | 'cookie' | 'secret' | 'localStorage' | 'sessionStorage'}`,
              )}
            </MetaDefault>
            {group.items.map((artifact) => (
              <CopyableArtifactRow
                key={`${artifact.kind}:${artifact.name}`}
                artifact={artifact}
                hideCopy={hideCopy}
              />
            ))}
          </VStack>
        ))}
      </VStack>
    </VStack>
  );
}
